import { z } from 'zod';
import type { WorkoutFeeling, WorkoutTypeKey } from './Workout';
import { localISODate } from '@/utils/format';

const WORKOUT_TYPE_KEYS = [
  'run',
  'cycle',
  'swim',
  'row',
  'walk',
  'other',
] as const satisfies readonly WorkoutTypeKey[];
const FEELING_KEYS = [
  'great',
  'good',
  'ok',
  'bad',
  'awful',
] as const satisfies readonly WorkoutFeeling[];

export const loginSchema = z.object({
  email: z.string().trim().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const registerSchema = z
  .object({
    firstName: z.string().trim().min(1, 'First name is required'),
    lastName: z.string().trim().min(1, 'Last name is required'),
    email: z.string().trim().email('Enter a valid email'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });
export type RegisterInput = z.infer<typeof registerSchema>;

/**
 * Numeric inputs are kept as strings so empty fields render the placeholder
 * instead of a pre-filled 0; conversion to numbers happens on submit.
 */
const optionalNumber = (max: number, message: string, integer = false) =>
  z
    .string()
    .trim()
    .refine((v) => {
      if (v === '') return true;
      const n = Number(v);
      if (!Number.isFinite(n) || n < 0 || n > max) return false;
      return integer ? Number.isInteger(n) : true;
    }, message);

export const workoutSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required'),
    type: z.enum(WORKOUT_TYPE_KEYS),
    date: z
      .string()
      .min(1, 'Date is required')
      .refine((v) => v <= localISODate(), 'Date can’t be in the future'),
    durationMin: z
      .string()
      .trim()
      .min(1, 'Duration is required')
      .refine((v) => {
        const n = Number(v);
        return Number.isInteger(n) && n >= 1 && n <= 1440;
      }, 'Duration must be 1–1440 minutes'),
    distanceKm: optionalNumber(999, 'Distance must be 0–999 km'),
    avgHeartRate: optionalNumber(250, 'Avg HR must be 0–250 bpm', true),
    maxHeartRate: optionalNumber(250, 'Max HR must be 0–250 bpm', true),
    feeling: z.enum(FEELING_KEYS),
  })
  .refine(
    (d) =>
      d.avgHeartRate === '' ||
      d.maxHeartRate === '' ||
      Number(d.maxHeartRate) >= Number(d.avgHeartRate),
    { message: 'Max HR can’t be below avg HR', path: ['maxHeartRate'] },
  );
export type WorkoutInput = z.infer<typeof workoutSchema>;
